import { MESSAGES } from '~/utils/message';

const { CREATE } = MESSAGES.SETTINGS.WEBHOOKS;

// Keys match the contact fields the webhook API accepts in `contactFields`. `payloadKey` is where
// the field sits in the QR scan / PURL visit contact block (olc-server WEBHOOK_CONTACT_FIELD_KEYS).
const CONTACT_FIELD_OPTIONS = [
  { field: 'firstName', payloadKey: 'contact_first', title: CREATE.ADDITIONAL_FIELD_FIRST_NAME },
  { field: 'lastName', payloadKey: 'contact_last', title: CREATE.ADDITIONAL_FIELD_LAST_NAME },
  {
    field: 'companyName',
    payloadKey: 'contact_company',
    title: CREATE.ADDITIONAL_FIELD_COMPANY_NAME,
  },
];

const CONTACT_PREFIX = 'contact:';
const CUSTOM_PREFIX = 'custom:';

/**
 * One list for the dropdown: the contact fields first, then the account's custom fields.
 * Option ids are prefixed strings so a contact field can never collide with a custom field id.
 * `sampleKey` is the key the field has in a delivered payload: the contact block key for a contact
 * field (`contact_first`), the field's own name for a custom field.
 */
export const buildAdditionalFieldOptions = (customFields = []) => [
  ...CONTACT_FIELD_OPTIONS.map(({ field, payloadKey, title }) => ({
    id: `${CONTACT_PREFIX}${field}`,
    title,
    sampleKey: payloadKey,
  })),
  ...customFields.map(({ id, value }) => ({
    id: `${CUSTOM_PREFIX}${id}`,
    title: value,
    sampleKey: value,
  })),
];

/** Splits the selected option ids into the `contactFields` / `customFieldIds` the API expects. */
export const toAdditionalFields = (selectedIds = []) => ({
  contactFields: selectedIds
    .filter((id) => id.startsWith(CONTACT_PREFIX))
    .map((id) => id.slice(CONTACT_PREFIX.length)),
  customFieldIds: selectedIds
    .filter((id) => id.startsWith(CUSTOM_PREFIX))
    .map((id) => Number(id.slice(CUSTOM_PREFIX.length))),
});

/** Selected option ids for a saved webhook, exactly as stored. */
export const toSelectedFieldIds = (webhook) => [
  ...(webhook?.contactFields || []).map((field) => `${CONTACT_PREFIX}${field}`),
  ...(webhook?.customFieldIds || []).map((id) => `${CUSTOM_PREFIX}${id}`),
];

/** Labels of a saved webhook's additional fields, skipping custom fields that were deleted. */
export const getSelectedFieldLabels = (webhook, options) => {
  const availableIds = keepAvailableFieldIds(toSelectedFieldIds(webhook), options);

  return options.filter(({ id }) => availableIds.includes(id)).map(({ title }) => title);
};

/**
 * Drops ids with no matching option, such as a deleted custom field. Filtering here rather than
 * when the webhook loads keeps saved fields intact if the custom field list arrives later.
 */
export const keepAvailableFieldIds = (selectedIds, options) => {
  const availableIds = new Set(options.map(({ id }) => id));

  return selectedIds.filter((id) => availableIds.has(id));
};

const SAMPLE_CUSTOM_FIELD_VALUE = 'Sample value';

// Events whose sample carries a contact block that olc-server shapes per webhook. `order_items.updated`
// is left out on purpose: the Laravel webhook server builds it, and it is not confirmed to honor the
// selection, so its sample must not promise fields.
const SAMPLE_CONTACT_BLOCKS = {
  'order_item.qr_code_scanned': 'scanned_by',
  'order_item.purl_visited': 'visited_by',
};

const CONTACT_SAMPLE_KEYS = CONTACT_FIELD_OPTIONS.map(({ payloadKey }) => payloadKey);

/** Payload keys of the ticked fields, split into contact block keys and custom field names. */
export const getSelectedFieldKeys = (selectedIds, options) => {
  const selected = options.filter(({ id }) => selectedIds.includes(id));
  const keysOf = (prefix) =>
    selected.filter(({ id }) => id.startsWith(prefix)).map(({ sampleKey }) => sampleKey);

  return { contactKeys: keysOf(CONTACT_PREFIX), customKeys: keysOf(CUSTOM_PREFIX) };
};

/**
 * Shapes an event's sample the way olc-server shapes the delivered payload. The contact block always
 * carries first name, last name and company; ticking any of them keeps only the ticked ones. Custom
 * fields are added under `custom_fields`, and the object is left out when none are ticked.
 * Events not shaped by olc-server are returned unchanged.
 */
export const applyAdditionalFieldsToSample = (eventName, data, { contactKeys, customKeys }) => {
  const blockKey = SAMPLE_CONTACT_BLOCKS[eventName];
  if (!blockKey || !data?.[blockKey]) return data;

  const block = { ...data[blockKey] };

  if (contactKeys.length) {
    CONTACT_SAMPLE_KEYS.filter((key) => !contactKeys.includes(key)).forEach((key) => {
      delete block[key];
    });
  }

  if (customKeys.length) {
    block.custom_fields = Object.fromEntries(
      customKeys.map((key) => [key, SAMPLE_CUSTOM_FIELD_VALUE])
    );
  }

  return { ...data, [blockKey]: block };
};
